import { FormikProps } from 'formik';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CeremonyCell } from './ceremonyCell';
import { CremationCell } from './cremationCell';
import { DateTimeInfo } from './DateTimeInfo';
import { IntermentCell } from './intermentCell';
import { PrivacityCell } from './PrivacityCell';
import { UNASSIGNED_RECORD_KEY } from './UNASSIGNED_RECORD_KEY';
import { VigilCell } from './vigilCell';
import FilteredPage from 'components/base/FilteredPage';
import { NoData } from 'components/base/NoData';
import SideFilterField from 'components/base/SideFilters/SideFilterField';
import { TableColumn } from 'components/base/Table/types';
import { excludeDirectionCenter } from 'components/record/Form/excludeDirectionCenter';
import config from 'config';

import { SimpleUserRequestPermissionNames } from 'config/apiFunus/generated/data-contracts';
import {
  RecordsServicesProps,
  IFilter,
  RecordInformationTypeFilter,
  RecordServiceDtoType,
} from 'config/apiTypes';
import { RecordServiceDto } from 'config/RecordServiceDto';
import './index.scss';
import { useCenters } from 'hooks/useCenters';
import useUsers from 'hooks/useUsers';
import { DeathType } from 'models/DeathType';
import { IMasterDataDto } from 'models/MasterData';
import { SimpleUser } from 'models/SimpleUser';

const serviceTypes: RecordInformationTypeFilter[] = [
  RecordInformationTypeFilter.APPOINTMENT,
  RecordInformationTypeFilter.COLLECTION,
  RecordInformationTypeFilter.CEREMONY,
  RecordInformationTypeFilter.CREMATION,
  RecordInformationTypeFilter.INTERMENT,
  RecordInformationTypeFilter.VIGIL,
];

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const InformationTable = () => {
  const { t } = useTranslation();
  const { users } = useUsers([SimpleUserRequestPermissionNames.ADVICE_ADD]);
  const { centers } = useCenters();

  const search = (params: IFilter<RecordsServicesProps>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const negotiatorUsername = (params.filter[0]?.negotiatorUsername as any)?.code;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const center = (params.filter[0]?.center as any)?.code;

    params.filter.splice(0, 1, {
      ...params.filter[0],
      center,
      negotiatorUsername,
    });

    let newSort = params.sort || [];
    if (newSort[0]?.name === 'vigil') {
      newSort = [
        {
          descending: newSort[0]?.descending,
          name: 'vigil.country',
        },
        {
          descending: newSort[0]?.descending,
          name: 'vigil.province',
        },
        {
          descending: newSort[0]?.descending,
          name: 'vigil.city',
        },
        {
          descending: newSort[0]?.descending,
          name: 'vigil.location',
        },
      ];
    }

    if (newSort[0]?.name === 'ceremony') {
      newSort = [
        {
          descending: newSort[0]?.descending,
          name: 'ceremony.country',
        },
        {
          descending: newSort[0]?.descending,
          name: 'ceremony.province',
        },
        {
          descending: newSort[0]?.descending,
          name: 'ceremony.city',
        },
        {
          descending: newSort[0]?.descending,
          name: 'ceremony.location',
        },
      ];
    }

    if (newSort[0]?.name === 'type') {
      newSort = [
        {
          descending: newSort[0]?.descending,
          name: 'interment.country',
        },
        {
          descending: newSort[0]?.descending,
          name: 'interment.province',
        },
        {
          descending: newSort[0]?.descending,
          name: 'interment.city',
        },
        {
          descending: newSort[0]?.descending,
          name: 'interment.location',
        },
      ];
    }

    return config.apiFunus.record.services({
      ...params,
      sort: newSort,
    });
  };

  const informationSearchFields = useCallback((
    formikProps: FormikProps<RecordsServicesProps>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    types: any[],
    assessors: SimpleUser[],
  ): ReactElement[] => [
    <SideFilterField
      key="erpId"
      filterType="input"
      formikProps={formikProps}
      name="erpId"
      placeholder={`${t('record.number')}`}
    />,
    <SideFilterField
      key="deceasedName"
      filterType="input"
      formikProps={formikProps}
      name="deceasedName"
      placeholder={`${t('common.name')} ${t('common.deceased')}`}
    />,
    <SideFilterField
      key="deceasedFirstSurname"
      filterType="input"
      formikProps={formikProps}
      name="deceasedFirstSurname"
      placeholder={`${t('common.firstSurname')} ${t('common.deceased')}`}
    />,
    <SideFilterField
      key="deceasedSecondSurname"
      filterType="input"
      formikProps={formikProps}
      name="deceasedSecondSurname"
      placeholder={`${t('common.secondSurname')} ${t('common.deceased')}`}
    />,
    <SideFilterField
      key="type"
      filterType="select"
      formikProps={formikProps}
      getLabel={(value) => t(`record.informationFilterType.${value}`)}
      getValue={(value) => value}
      name="type"
      options={types}
      placeholder={`${t('common.type')}`}
    />,
    <SideFilterField
      key="date"
      disabled={!formikProps.values.type}
      filterType="date"
      formikProps={formikProps}
      name="date"
      placeholder={`${t('common.date')}`}
    />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <SideFilterField<any, IMasterDataDto>
      key="negotiatorUsername"
      filterType="select"
      formikProps={formikProps}
      getLabel={({ description }) => description}
      getValue={({ code }) => code}
      name="negotiatorUsername"
      options={[
        {
          code: UNASSIGNED_RECORD_KEY,
          description: t('record.unAssignedRecords'),
        },
        ...assessors
          .map(({
            username, name, firstSurname, secondSurname,
          }): IMasterDataDto => ({
            code: username,
            description: `${name} ${firstSurname} ${secondSurname}`,
          })),
      ]}
      placeholder={t('record.advisers')}
    />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <SideFilterField<any, IMasterDataDto>
      key="center"
      filterType="select"
      formikProps={formikProps}
      getLabel={({ description }) => description}
      getValue={({ code }) => code}
      name="center"
      options={centers.filter(excludeDirectionCenter)}
      placeholder={t('record.deathData.destinationCenter')}
    />,
  ], [centers, t]);

  const columns = useMemo(
    (): TableColumn<RecordServiceDto>[] => [
      {
        accessor: 'erpId',
        Cell: ({ cell: { value: val, row } }) => (row.original.recordId
          ? <Link to={`/records/${row.original.recordId}/edit`}>{val}</Link>
          : val),
        Header: `${t('record.number')}`,
        sortable: true,
      },
      {
        accessor: 'deceasedName',
        Cell: ({ cell: { row: { original: { person: { name, firstSurname, secondSurname } } } } }) => `${name || ''} ${firstSurname || ''} ${secondSurname || ''}`,
        Header: `${t('common.name')}`,
        sortable: true,
      },
      {
        accessor: 'appointmentDate',
        Cell: ({ cell: { row: { original: { appointmentDateTime } } } }) => (appointmentDateTime
          ? <DateTimeInfo date={appointmentDateTime} time={appointmentDateTime} />
          : <NoData />),
        className: 'text-align-center',
        Header: `${t('record.appointment')}`,
        sortable: true,
      },
      {
        accessor: 'collectionDateTime',
        Cell: ({ cell: { value: val } }) => (val
          ? <DateTimeInfo date={val} time={val} />
          : <NoData />),
        className: 'text-align-center',
        Header: `${t('record.pickupDate')}`,
        sortable: true,
      },
      {
        accessor: 'preparationStatus',
        Cell: ({ cell: { value: val } }) => (val ? t(`record.preparationStatusOptions.${val}`) : <NoData />),
        className: 'text-align-center',
        Header: `${t('record.preparationStatus')}`,
        sortable: true,
      },
      {
        accessor: 'ageConfidential',
        Cell: (
          { cell: { row: { original: { ageConfidential, publishCeremony, publishVigil } } } },
        ) => (
          <PrivacityCell
            ageConfidential={ageConfidential}
            publishCeremony={publishCeremony}
            publishVigil={publishVigil}
          />
        ),
        className: 'text-align-center privacity-column',
        Header: `${t('common.privacity')}`,
      },
      {
        accessor: 'vigil',
        Cell: ({ cell: { row: { original: { vigil } } } }) => (vigil
          ? <VigilCell value={vigil} />
          : <NoData />),
        className: 'text-align-center',
        Header: `${t('service.vigil.vigil')}`,
        sortable: true,
      },
      {
        accessor: 'ceremony',
        Cell: ({ cell: { value: val } }) => (val
          ? <CeremonyCell value={val} />
          : <NoData />),
        className: 'text-align-center',
        Header: `${t('service.ceremony.ceremony')}`,
        sortable: true,
      },
      {
        accessor: 'type',
        Cell: ({ row: { original: { cremation, interment, type } } }) => {
          if (type === RecordServiceDtoType.CREMATION && cremation) {
            return (<CremationCell value={cremation} />);
          } if (type === RecordServiceDtoType.INTERMENT && interment) {
            return (<IntermentCell value={interment} />);
          }

          return (<NoData />);
        },
        className: 'text-align-center',
        Header: `${t('service.cremation.cremation')}/${t('service.interment.interment')}`,
        sortable: true,
      },
    ],
    [t],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowClassName = (index: any, row: RecordsServicesProps) => (row.deathType === DeathType.judicial ? 'is-judicial-death' : '');

  return (
    <FilteredPage<RecordsServicesProps, RecordServiceDto>
      apiCall={(params) => search(params)}
      className="f-information-table"
      columns={columns}
      fields={(props) => informationSearchFields(
        props,
        serviceTypes,
        users,
      )}
      initialValues={{
        date: undefined,
        deathType: undefined,
        deceasedFirstSurname: undefined,
        deceasedName: undefined,
        deceasedSecondSurname: undefined,
        erpId: undefined,
        negotiatorUsername: undefined,
        type: undefined,
      }}
      rowClassName={rowClassName}
      text={{
        search: t('record.search'),
        title: t('information.title'),
      }}
    />
  );
};
